import { Component, HostListener, OnInit, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { environment as env } from '../../../environments/environment';
import { agGridThemes } from '../../@shared/ag-grid-themes';
import { SseBurstBreaker } from '../shared/sse-burst-breaker';
import { UserPrefsService } from '../../@services/user-prefs.service';
import { CustomerColorsService } from '../../@services/customer-colors.service';

interface CommerceEvent {
  _id: string;
  created_at: string;
  customer: string;
  integration: 'woocommerce' | 'stripe';
  topic?: string;
  status?: string;
  action: 'activate' | 'deactivate' | 'noop';
  // `already_activated` / `already_deactivated` — the underlying
  // Cognito state already matched what the webhook wanted. Emitted
  // alongside action:'noop' from all commerce dispatchers 2026-07-13.
  // Distinct from plain 'noop' (which means "we intentionally did
  // nothing, event was not entitlement-changing").
  //
  // `test` — event was a reachability fire from the upstream provider's
  // test button (Kartra IPN "Test order sale", WC "Test webhook",
  // SamCart "Send Test"). Dispatcher short-circuited before executing;
  // the row's `action` reflects what a REAL event of that shape WOULD
  // have done (activate/deactivate/noop) so operators can verify the
  // product_map → scanner mapping straight off the grid.
  result: 'success' | 'partial' | 'failed' | 'noop' | 'already_activated' | 'already_deactivated' | 'test';
  email?: string;
  first_name?: string;
  last_name?: string;
  scanners?: string[];
  line_items?: Array<{ product_id?: number | string; name?: string }>;
  error?: string;
  details?: Record<string, any>;
  // Backend-populated endpoint hint — surfaces in the detail panel so
  // ops can tell woocommerce vs stripe REST paths apart at a glance
  // (commit 816c4ce added the template binding; the type declaration
  // was missed and Vercel's tsc caught it on the next build).
  endpoint?: string;
  // Backend-populated per-event action_summary — one-line narrative of
  // what the underlying service action accomplished. Surfaced in the
  // detail panel as "What we did:" (commit 0a70e14). Same declaration
  // miss as `endpoint` above; template binding shipped without the
  // interface field, Vercel's tsc caught it on the next build.
  action_summary?: string;
  // Backend-populated identifier of the code path / bridge that emitted
  // this row. Distinguishes hardwired paths (`wp-bridge:sibyl-optionpit`,
  // `keap-bridge:theotrade`) from the customer-agnostic controllers
  // (`partner_api:activate`, `woocommerce:webhook`) so ops can tell
  // them apart at a glance. Optional — pre-2026-07-08 rows omit it and
  // the detail panel *ngIf-guards render so blank labels don't show.
  // Callers populate as they get touched. Mike 2026-07-08.
  source?: string;
  // Client-only flash marker — mirrors error-reports.
  _freshUntil?: number;
}

const LIVE_LOCALSTORAGE_KEY = 'commerce_events_live_on';
// Time an incoming SSE row keeps its `_freshUntil` bloom before the row-
// class-rule drops it back to baseline styling. Kept from the pre-134
// pageStack implementation.
const LIVE_HIGHLIGHT_MS = 2000;
// Server-side pagination block size (IT-F2-134). Matches MongoExplorer's
// value from IT-F2-95 — 100 balances round-trip cost vs per-scroll fetch
// chatter. maxBlocksInCache caps memory so a 100k-doc audit history never
// loads more than ~1000 rows worth into JS.
const CACHE_BLOCK_SIZE = 100;
const MAX_BLOCKS_IN_CACHE = 10;

@Component({
  standalone: true,
  selector: 'app-commerce-events',
  imports: [AgGridAngular, CommonModule, FormsModule],
  templateUrl: './commerce-events.component.html',
  styleUrl: './commerce-events.component.scss',
})
export class CommerceEventsComponent implements OnInit, OnDestroy {

  // Lookup-by-ID
  lookupId = '';
  lookupErr: string | null = null;
  loadingLookup = false;

  // Listing — with server-side pagination (IT-F2-134) the grid manages
  // rows via its datasource; the client-side `list` array is only kept
  // around for the copy-JSON / copy-CSV helpers on the in-view block.
  loadingList = false;
  listErr: string | null = null;
  // Total row count for the current filter — populated from the
  // first-block fetch's `total` field (server-side countDocuments). Null
  // until the grid finishes its first pull.
  totalCount: number | null = null;

  // Detail
  selected: CommerceEvent | null = null;

  // ag-grid
  theme = agGridThemes.dark;
  grid: any;
  selectedCount = 0;

  // Live mode
  liveOn = false;
  liveSuspended = false;
  // Datasource wire-race guard: on first mount the <ag-grid-angular> is
  // added to the DOM by *ngIf during the same async flow that resolves
  // the initial load. AG-Grid's onGridReady may not have fired by the
  // time we're ready to install the datasource — stash it here and let
  // BOTH the init flow AND onGridReady try to install; whichever
  // completes second is a no-op.
  private _pendingDatasource: any = null;

  private _eventSource: EventSource | null = null;
  private _reconnectTimer: any = null;
  private _whenRefreshTimer: any = null;
  private readonly apiBase = `${env.apiUrl}/rest`;

  // ms-since-epoch of last-seen at the moment the operator navigated
  // here. See sibling implementation in error-reports.component for
  // why this is captured ONCE rather than read live from UserPrefsService.
  private _arrivalSeenMs: number = 0;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private zone: NgZone,
    private userPrefs: UserPrefsService,
    private customerColors: CustomerColorsService,
  ) {
    const navSnap = this.userPrefs.arrivalSnapshot('commerce_events');
    if (navSnap) {
      this._arrivalSeenMs = Date.parse(navSnap);
    } else {
      this.userPrefs.beforeMarkSeen('commerce_events');
      const fallback = this.userPrefs.arrivalSnapshot('commerce_events');
      this._arrivalSeenMs = fallback ? Date.parse(fallback) : 0;
      this.userPrefs.markSeen('commerce_events');
    }
    this.customerColors.hydrate().then(() => {
      this.grid?.api?.redrawRows?.();
    });
    this.init_grid();
  }

  private _isNewSinceArrival(row: any): boolean {
    if (!this._arrivalSeenMs || !row?.created_at) return false;
    const t = Date.parse(String(row.created_at));
    return Number.isFinite(t) && t > this._arrivalSeenMs;
  }

  // Sibling to error-reports rowStyleForNew. Three layers:
  //   - per-row --row-flash-color for the live-row-fresh 2s pulse
  //   - zebra 6%/3% baseline tint (Users page convention)
  //   - 18% NEW-since-arrival tint that overrides the zebra
  rowStyleForNew = (params: any) => {
    const slug = params?.data?.customer;
    const style: any = { '--row-flash-color': this.customerColors.flashRgb(slug) };
    if (this._isNewSinceArrival(params?.data)) {
      style.backgroundColor = this.customerColors.newRowTint(slug, 0.18);
    } else {
      const zebra = this.customerColors.rowZebraTint(slug, params?.node?.rowIndex || 0);
      if (zebra) style.backgroundColor = zebra;
    }
    return style;
  };

  async ngOnInit() {
    try { this.liveOn = localStorage.getItem(LIVE_LOCALSTORAGE_KEY) === '1'; } catch { /* ignore */ }
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.lookupId = id;
      await this.onLookup();
    }
    // IT-F2 Mike 2026-11-02 — parse ?filter= + ?sort= (base64 JSON) off
    // the URL so a deep-linked view reproduces the operator's filtered
    // + sorted grid on first paint. Stash into _pendingFilterModel /
    // _pendingSortModel so _applyPendingGridState can land them on
    // the grid as soon as onGridReady fires (datasource then rebuilds
    // itself with the applied filter via the ag-grid built-in flow).
    const q = this.route.snapshot.queryParamMap;
    const filterParam = q.get('filter') || '';
    const sortParam = q.get('sort') || '';
    if (filterParam) {
      const fm = this._jsonFromB64(filterParam);
      if (fm && typeof fm === 'object') this._pendingFilterModel = fm;
    }
    if (sortParam) {
      const sm = this._jsonFromB64(sortParam);
      if (Array.isArray(sm)) this._pendingSortModel = sm;
    }
    // Wire the datasource. Whether onGridReady has fired yet or not,
    // _wireDatasourceIfReady is idempotent — it either installs now or
    // stashes for onGridReady to install shortly.
    this._pendingDatasource = this._buildDatasource();
    this._wireDatasourceIfReady();
    if (this.liveOn) this._open_event_source();
    this._start_when_refresh();
  }

  // Burst-breaker — drop incoming SSE events when rate exceeds threshold
  // and queue ONE full reload after the storm. Defensive against any
  // future upstream burst (e.g. a wp-cron WooCommerce backfill blasting
  // hundreds of subscription-event docs at once). Infinite-model reload
  // = `refreshInfiniteCache()` (purges cache + refetches block 0).
  private _sseBurstBreaker = new SseBurstBreaker({
    onPostStormRefresh: () => this._refreshGridFromServer(),
  });

  ngOnDestroy() {
    this._close_event_source();
    this._sseBurstBreaker.dispose();
    if (this._whenRefreshTimer) {
      clearTimeout(this._whenRefreshTimer);
      clearInterval(this._whenRefreshTimer);
      this._whenRefreshTimer = null;
    }
  }

  private _start_when_refresh() {
    const tick = () => {
      const api = this.grid?.api;
      if (api?.refreshCells) {
        api.refreshCells({ columns: ['created_at'], force: true });
      }
    };
    const msUntilNextMinute = 60_000 - (Date.now() % 60_000);
    this.zone.runOutsideAngular(() => {
      this._whenRefreshTimer = setTimeout(() => {
        tick();
        this._whenRefreshTimer = setInterval(tick, 60_000);
      }, msUntilNextMinute);
    });
  }

  /**
   * Refresh button + external triggers (post-storm SSE reload, live-doc
   * arrival) call this to force the infinite-model grid to re-pull from
   * the server. `refreshInfiniteCache()` purges the block cache and
   * refetches block 0 with the current filter/sort model — count=1
   * fires again so the toolbar total updates too.
   */
  async loadList() {
    this._refreshGridFromServer();
  }

  private _refreshGridFromServer() {
    const api = this.grid?.api;
    if (!api?.refreshInfiniteCache) return;
    try {
      api.refreshInfiniteCache();
    } catch (err: any) {
      console.warn('commerce-events refreshInfiniteCache failed:', err?.message);
    }
  }

  private _b64Json(o: any): string {
    try { return btoa(unescape(encodeURIComponent(JSON.stringify(o)))); }
    catch { return btoa(JSON.stringify(o)); }
  }

  // IT-F2 Mike 2026-11-02 chat: "i need the ecommerce event viewer to
  // remember the filters applied in the url without changing the
  // history". URL round-trip helpers for filter + sort model via
  // base64-encoded JSON (same encoding as the Export CSV filter
  // param), decoded on first paint + re-written on every grid filter /
  // sort change through replaceUrl so dropdown fiddling doesn't spam
  // browser history.
  private _jsonFromB64(b64: string): any {
    try { return JSON.parse(decodeURIComponent(escape(atob(b64)))); }
    catch { try { return JSON.parse(atob(b64)); } catch { return null; } }
  }
  // Pending-apply state so the filter/sort read on ngOnInit lands on
  // the grid as soon as it mounts (onGridReady), without racing
  // against ag-grid's initialization.
  private _pendingFilterModel: any = null;
  private _pendingSortModel: any[] | null = null;
  private _applyPendingGridState() {
    const api = this.grid?.api;
    if (!api) return;
    if (this._pendingFilterModel && Object.keys(this._pendingFilterModel).length > 0) {
      try { api.setFilterModel?.(this._pendingFilterModel); } catch { /* ignore */ }
      this._pendingFilterModel = null;
    }
    if (Array.isArray(this._pendingSortModel) && this._pendingSortModel.length > 0) {
      try {
        const colState = this._pendingSortModel.map((s: any, idx: number) => ({
          colId: s.colId, sort: s.sort, sortIndex: idx,
        }));
        api.applyColumnState?.({ state: colState, defaultState: { sort: null } });
      } catch { /* ignore */ }
      this._pendingSortModel = null;
    }
  }
  // Called by onFilterChanged / onSortChanged. Serializes the current
  // grid state to ?filter + ?sort query params via replaceUrl so a
  // shared URL reproduces the view AND dropdown fiddling doesn't
  // pollute browser history.
  private _syncGridStateToUrl() {
    const api = this.grid?.api;
    if (!api) return;
    const filterModel = api.getFilterModel?.() || {};
    const sortModel = api.getColumnState?.()
      ?.filter((c: any) => c?.sort)
      ?.sort((a: any, b: any) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      ?.map((c: any) => ({ colId: c.colId, sort: c.sort })) || [];
    const hasFilter = filterModel && Object.keys(filterModel).length > 0;
    const hasSort = Array.isArray(sortModel) && sortModel.length > 0;
    try {
      this.router?.navigate([], {
        relativeTo: this.route,
        queryParams: {
          filter: hasFilter ? this._b64Json(filterModel) : null,
          sort: hasSort ? this._b64Json(sortModel) : null,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    } catch { /* router may be undefined pre-ngOnInit; no-op */ }
  }

  /**
   * AG-Grid infinite-model datasource. Each block pull (100 rows by
   * default, see CACHE_BLOCK_SIZE) fires one GET
   * /admin/commerce-events?skip=N&limit=100 with the current
   * filter/sort model base64-json-encoded. First-block pull (startRow=0)
   * adds count=1 so the toolbar total reflects the current filtered set.
   *
   * The row-model handles all the row insertion — DO NOT bind `[rowData]`
   * on the template. Sort/filter changes cause AG-Grid to reset back to
   * block 0 automatically.
   */
  private _buildDatasource(): any {
    return {
      rowCount: undefined,
      getRows: async (params: any) => {
        const startRow = params.startRow || 0;
        const endRow = params.endRow || (startRow + CACHE_BLOCK_SIZE);
        const blockSize = endRow - startRow;
        const filterModel = params.filterModel || {};
        const sortModel = params.sortModel || [];
        const includeCount = startRow === 0;

        this.loadingList = true;
        this.listErr = null;

        const parts: string[] = [
          `skip=${startRow}`,
          `limit=${blockSize}`,
        ];
        if (filterModel && Object.keys(filterModel).length > 0) {
          parts.push(`filter=${encodeURIComponent(this._b64Json(filterModel))}`);
        }
        if (Array.isArray(sortModel) && sortModel.length > 0) {
          parts.push(`sort=${encodeURIComponent(this._b64Json(sortModel))}`);
        }
        if (includeCount) parts.push('count=1');

        const url = `${this.apiBase}/admin/commerce-events?${parts.join('&')}`;
        try {
          const res: any = await this.http.get(url, { withCredentials: true }).toPromise();
          // Endpoint returns `{rows, skip, limit, count, total?}` when any
          // of the new params are set; falls back to a plain array when
          // called with pre-134 param set. Tolerate both shapes.
          const rows: CommerceEvent[] = Array.isArray(res) ? res : (Array.isArray(res?.rows) ? res.rows : []);
          const total = (res && typeof res.total === 'number') ? res.total : undefined;

          // Tell grid the exact last row when we know the total, else -1
          // to keep the "loading more" scrollbar working, unless we just
          // received less than a full block (last block).
          let lastRow = -1;
          if (typeof total === 'number') {
            lastRow = total;
            this.totalCount = total;
          } else if (rows.length < blockSize) {
            lastRow = startRow + rows.length;
          }
          params.successCallback(rows, lastRow);
        } catch (e: any) {
          this.listErr = e?.error?.message || e?.message || 'load failed';
          this.totalCount = null;
          params.failCallback();
        } finally {
          this.loadingList = false;
        }
      },
    };
  }

  private _wireDatasourceIfReady() {
    const api = this.grid?.api;
    const ds = this._pendingDatasource;
    if (!api || !ds) return;
    // v33+ API — setDatasource is deprecated. setGridOption purges the
    // block cache automatically so a fresh getRows(startRow=0) will fire.
    api.setGridOption('datasource', ds);
    this._pendingDatasource = null;
  }

  toggleLive() {
    this.liveOn = !this.liveOn;
    try { localStorage.setItem(LIVE_LOCALSTORAGE_KEY, this.liveOn ? '1' : '0'); } catch { /* ignore */ }
    if (this.liveOn && !this.liveSuspended) this._open_event_source();
    else this._close_event_source();
  }

  private async _open_event_source() {
    if (this._eventSource) return;
    if (this._reconnectTimer) return;
    let token: string;
    try {
      const mintUrl = `${this.apiBase}/admin/commerce-events/stream-token`;
      const res: any = await this.http.post(mintUrl, {}, { withCredentials: true }).toPromise();
      token = res?.token;
      if (!token) throw new Error('mint response missing token');
    } catch (e: any) {
      console.warn('commerce-events stream-token mint failed:', e?.message);
      this._schedule_sse_reconnect();
      return;
    }
    if (!this.liveOn || this.liveSuspended) return;
    const url = `${this.apiBase}/admin/commerce-events/stream?token=${encodeURIComponent(token)}`;
    try {
      const es = new EventSource(url);
      this._eventSource = es;
      es.onmessage = (ev: MessageEvent) => {
        if (!ev?.data) return;
        if (this._sseBurstBreaker.shouldDrop()) return;
        try {
          const doc = JSON.parse(ev.data);
          this.zone.run(() => this._apply_live_doc(doc));
        } catch { /* malformed */ }
      };
      es.onerror = () => {
        console.warn('commerce-events stream error; will mint fresh token + reopen');
        this._close_event_source();
        this._schedule_sse_reconnect();
      };
    } catch (e: any) {
      console.warn('commerce-events stream open failed:', e?.message);
      this._eventSource = null;
      this._schedule_sse_reconnect();
    }
  }

  private _schedule_sse_reconnect() {
    if (this._reconnectTimer) return;
    if (!this.liveOn || this.liveSuspended) return;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this.liveOn && !this.liveSuspended) this._open_event_source();
    }, 3000);
  }

  private _close_event_source() {
    if (this._eventSource) {
      try { this._eventSource.close(); } catch { /* ignore */ }
      this._eventSource = null;
    }
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  private _apply_live_doc(doc: CommerceEvent) {
    if (!doc || !doc._id) return;
    // Infinite row model doesn't support applyTransaction add — the
    // right way to surface a new SSE-arrived row is to refresh the
    // cache so block 0 refetches (server ORDER BY created_at DESC will
    // slot the new doc at the top). Debounce isn't needed here: the
    // burst-breaker already covers the storm case, and single-doc
    // arrivals are cheap.
    this._refreshGridFromServer();
  }

  async onLookup() {
    this.lookupErr = null;
    this.selected = null;
    const raw = (this.lookupId || '').trim();
    if (!raw) {
      this.lookupErr = 'Paste an event ID to look up.';
      return;
    }
    // Tolerate "Event ID: <hex>" pasted with a prefix.
    const m = raw.match(/[a-fA-F0-9]{24}/);
    const id = m ? m[0] : raw;
    this.loadingLookup = true;
    try {
      const url = `${this.apiBase}/admin/commerce-events/${encodeURIComponent(id)}`;
      const res: any = await this.http.get(url, { withCredentials: true }).toPromise();
      this.selected = res;
    } catch (e: any) {
      if (e?.status === 404) {
        this.lookupErr = `No event found for ${id}. Records auto-expire after 365 days.`;
      } else {
        this.lookupErr = e?.error?.message || e?.message || 'lookup failed';
      }
    } finally {
      this.loadingLookup = false;
    }
  }

  async onSelect(event: CommerceEvent) {
    this.lookupId = event._id;
    this.lookupErr = null;
    this.selected = event;
  }

  prettyJson(o: any): string {
    try { return JSON.stringify(o, null, 2); }
    catch { return String(o); }
  }

  formatRelative(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return Math.floor(diff) + 's ago';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    return d.toISOString().slice(0, 16).replace('T', ' ');
  }

  /**
   * Full wall-clock timestamp for the "When" column's hover tooltip.
   * Column body shows "5m ago"; hover reveals the exact ET day/date/
   * time + a UTC line. Vanilla Intl to avoid pulling luxon into this
   * component. Mike 2026-07-06.
   */
  formatFullWhen(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const et = d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: true, timeZoneName: 'short',
    });
    const utc = d.toISOString().slice(0, 19).replace('T', ' ') + ' UTC';
    return `${et}\n${utc}`;
  }

  copyText(s: string) {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(s).catch(() => {});
    }
  }

  // Shareable deep-link URL to the selected commerce event.
  linkFor(id: string): string {
    if (typeof window === 'undefined') return `/admin/commerce-events/${id}`;
    return `${window.location.origin}/admin/commerce-events/${id}`;
  }

  copyLink(id: string) {
    this.copyText(this.linkFor(id));
  }

  clearSelection() {
    this.selected = null;
    this.lookupId = '';
    this.lookupErr = null;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.grid?.api?.sizeColumnsToFit?.();
  }

  // ---- Copy helpers ----
  // When the operator has rows checkbox-selected, use those; otherwise
  // use whatever's currently cached in the infinite-model view. In
  // infinite mode the "visible" set is a rolling window over the total
  // — copy operations naturally reflect that (the operator sees what
  // they're copying, no surprises). Selection wins when present.
  private getEffectiveRows(): CommerceEvent[] {
    const api = this.grid?.api;
    if (!api) return [];
    const selected = api.getSelectedRows?.() as CommerceEvent[] | undefined;
    if (selected && selected.length > 0) return selected;
    const rows: CommerceEvent[] = [];
    // Infinite-model equivalent — forEachNode iterates cached blocks
    // only. forEachNodeAfterFilterAndSort doesn't exist on the infinite
    // rowModel (it's a clientSide-model method).
    api.forEachNode?.((n: any) => { if (n?.data) rows.push(n.data as CommerceEvent); });
    return rows;
  }

  effectiveCount(): number {
    if (this.selectedCount > 0) return this.selectedCount;
    // Fall back to the loaded / cached row count. `totalCount` is the
    // full server-side count, which we'd double-count if we returned
    // it here vs the actual selected/visible rows the copy would emit.
    return this.getEffectiveRows().length;
  }

  copyButtonLabel(prefix: string): string {
    const n = this.effectiveCount();
    if (n === 0) return prefix;
    const noun = this.selectedCount > 0 ? 'selected' : 'visible';
    return `${prefix} (${n} ${noun})`;
  }

  onCopyJson() {
    const rows = this.getEffectiveRows();
    if (!rows.length) return;
    this.copyText(JSON.stringify(rows, null, 2));
  }

  onCopyIds() {
    const rows = this.getEffectiveRows();
    if (!rows.length) return;
    this.copyText(rows.map(r => r._id).join(', '));
  }

  /**
   * IT-F2-338: full-dataset CSV export beyond the visible page. Grabs the
   * grid's current filter/sort model, hits GET /admin/commerce-events/export
   * (which streams every matching row, hard-cap 100k service-side), and
   * triggers a browser file download. Live/paused state of the grid is
   * irrelevant — the endpoint reads Mongo, not the block cache.
   */
  exporting = false;
  // Split-button dropdown state — click main = default (csv), click chevron = pick format.
  exportMenuOpen = false;
  async onExportCsv(format: 'csv' | 'xls' = 'csv') {
    if (this.exporting) return;
    this.exportMenuOpen = false;
    const api = this.grid?.api;
    const filterModel = api?.getFilterModel?.() || {};
    const sortModel = api?.getColumnState?.()
      ?.filter((c: any) => c?.sort)
      ?.sort((a: any, b: any) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      ?.map((c: any) => ({ colId: c.colId, sort: c.sort })) || [];

    const parts: string[] = [`format=${encodeURIComponent(format)}`];
    if (filterModel && Object.keys(filterModel).length > 0) {
      parts.push(`filter=${encodeURIComponent(this._b64Json(filterModel))}`);
    }
    if (Array.isArray(sortModel) && sortModel.length > 0) {
      parts.push(`sort=${encodeURIComponent(this._b64Json(sortModel))}`);
    }
    const qs = `?${parts.join('&')}`;
    const url = `${this.apiBase}/admin/commerce-events/export${qs}`;

    this.exporting = true;
    try {
      const resp: any = await this.http.get(url, {
        withCredentials: true,
        responseType: 'blob' as 'json',       // Angular quirk: blob response type via cast
        observe: 'response' as 'body',
      }).toPromise();
      const blob: Blob = resp.body;
      const truncated = String(resp.headers?.get?.('X-Export-Truncated') || '') === 'true';
      const rowCount = Number(resp.headers?.get?.('X-Export-Row-Count') || '0');

      // Derive filename from Content-Disposition when present; fall back to
      // client-side stamp so a proxy that strips headers still gets a good name.
      let filename = '';
      const cd = String(resp.headers?.get?.('Content-Disposition') || '');
      const m = cd.match(/filename="?([^";]+)"?/i);
      if (m) filename = m[1];
      if (!filename) {
        // Fallback if Content-Disposition was stripped by a proxy. Match the
        // backend YYYY-MM-DD shape per Mike 2026-09-02.
        const today = new Date().toISOString().slice(0, 10);
        filename = `commerce-events-export-${today}.${format}`;
      }

      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // 60s window is enough for the browser to actually stream the blob
      // into the download flow — revoke earlier and Chrome sometimes aborts.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);

      if (truncated) {
        alert(`Export capped at 100,000 rows (${rowCount.toLocaleString()} downloaded). Narrow the filter and re-export to get the remainder.`);
      }
    } catch (e: any) {
      alert(`Export failed: ${e?.error?.message || e?.message || 'unknown error'}`);
    } finally {
      this.exporting = false;
    }
  }

  onCopyCsv() {
    const rows = this.getEffectiveRows();
    if (!rows.length) return;
    const headers = ['ID', 'When', 'Customer', 'Integration', 'Topic', 'Status', 'Action', 'Result', 'Email', 'Scanners', 'Error'];
    const esc = (v: unknown): string => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(esc).join(',')];
    for (const r of rows) {
      lines.push([
        r._id, r.created_at, r.customer, r.integration, r.topic, r.status, r.action, r.result,
        r.email, (r.scanners || []).join('|'), r.error,
      ].map(esc).join(','));
    }
    this.copyText(lines.join('\n'));
  }

  // ag-grid result/action color cellClass helpers — kept here so the
  // ColDef objects stay declarative and reusable for the live highlight.
  resultClass(result: string): string {
    switch (result) {
      case 'success':             return 'text-emerald-400';
      case 'failed':              return 'text-red-400';
      case 'partial':             return 'text-amber-400';
      case 'noop':                return 'text-gray-400';
      // Idempotent already-in-target-state — teal (positive-neutral)
      // for grants, slate (negative-neutral) for revokes so operators
      // can distinguish at a glance from a real success without them
      // looking loud.
      case 'already_activated':   return 'text-teal-300';
      case 'already_deactivated': return 'text-slate-400';
      // Provider test-button fire — violet so it stands out from every
      // real result color. Operators can filter the Result column for
      // "test" to find every ping in the grid.
      case 'test':                return 'text-violet-300';
      default:                    return '';
    }
  }
  actionClass(action: string): string {
    switch (action) {
      case 'activate':    return 'text-emerald-400';
      case 'deactivate':  return 'text-red-400';
      case 'noop':        return 'text-gray-400';
      default:            return '';
    }
  }

  /**
   * Build the right-click context menu items. When the right-clicked row
   * carries an email (or is part of a selection with emails), offer a
   * user-lookup deep-link into the Users panel. Multi-row selection →
   * plural label + comma-joined ?emails=; single row → single-email
   * shortcut. Falls through to ag-grid's standard copy/export items so
   * the menu still does the routine things.
   */
  private _buildContextMenu(params: any): any[] {
    const items: any[] = [];
    const api = params?.api;
    // Collect emails from the current selection first — if the
    // right-clicked row IS in the selection, that's what the operator
    // clearly wanted to act on. If the row isn't selected, fall back to
    // just its own email so a single right-click still works.
    const selected: any[] = api?.getSelectedRows?.() || [];
    const clickedRow: any = params?.node?.data || null;
    const clickedEmail = this._rowEmail(clickedRow);
    const clickedIsSelected = clickedRow && selected.some((r: any) => r?._id === clickedRow._id);

    // Column-scoped action: right-click on the Email cell → filter the
    // grid by that address. Sibling of error-reports.component's
    // filter-by-user pattern. Drives the standard text-column filter so
    // the header floating input reflects the applied state.
    const colId = params?.column?.getColId?.();
    if (colId === 'email' && clickedEmail) {
      items.push({
        name: `Filter events by ${this._htmlEscape(clickedEmail)}`,
        icon: '<span style="color:#60a5fa">⚲</span>',
        action: () => this.zone.run(async () => {
          if (!api) return;
          try {
            await Promise.resolve(api.setColumnFilterModel('email', {
              filterType: 'text', type: 'contains', filter: clickedEmail,
            }));
            api.onFilterChanged();
          } catch (err) {
            console.warn('Filter-by-email action failed', err);
          }
        }),
      });
      items.push('separator');
    }

    // Customer column filter — customer slugs are exact values, so
    // 'equals' is the right operator (a 'mta' filter shouldn't match
    // 'mta-preview' or similar). Empty/missing slugs are skipped.
    const clickedCustomer = String(params?.node?.data?.customer || '').trim();
    if (colId === 'customer' && clickedCustomer) {
      items.push({
        name: `Filter events by ${this._htmlEscape(clickedCustomer)}`,
        icon: '<span style="color:#60a5fa">⚲</span>',
        action: () => this.zone.run(async () => {
          if (!api) return;
          try {
            await Promise.resolve(api.setColumnFilterModel('customer', {
              filterType: 'text', type: 'equals', filter: clickedCustomer,
            }));
            api.onFilterChanged();
          } catch (err) {
            console.warn('Filter-by-customer action failed', err);
          }
        }),
      });
      items.push('separator');
    }

    let emailList: string[];
    if (clickedIsSelected && selected.length > 0) {
      emailList = this._dedupeEmails(selected.map((r: any) => this._rowEmail(r)));
    } else if (clickedEmail) {
      emailList = [clickedEmail];
    } else {
      emailList = [];
    }
    if (emailList.length > 0) {
      const label = emailList.length === 1
        ? `Look up ${this._htmlEscape(emailList[0])} in Users panel`
        : `Look up ${emailList.length} users in Users panel`;
      items.push({
        name: label,
        icon: '<span style="color:#60a5fa">↗</span>',
        action: () => {
          // Query string caps around ~2000 chars on most browsers +
          // Vercel proxies. Emails average 25 chars → we can safely
          // encode ~60 in one URL. Beyond that, warn the operator
          // and truncate rather than silently drop rows.
          const cap = 60;
          let list = emailList;
          if (list.length > cap) {
            const proceed = window.confirm(
              `${list.length} rows selected — URL would exceed browser limits.\n` +
              `Continue with the first ${cap} emails only?`,
            );
            if (!proceed) return;
            list = list.slice(0, cap);
          }
          const qs = list.map(e => encodeURIComponent(e)).join(',');
          // Open in a new tab so the Commerce Events grid state
          // (selection, filters, live position) stays intact.
          window.open(`/admin/users?emails=${qs}`, '_blank');
        },
      });
      items.push('separator');
    }
    // IT-F2-338 followup: override ag-grid's built-in `'export'` menu
    // item with our full-dataset CSV export. The built-in only sees rows
    // currently in the infinite-model's cache (CACHE_BLOCK_SIZE = 100)
    // so it silently caps at 100 rows regardless of how many match the
    // filter — Mike hit this immediately after ship. Route the menu
    // click through onExportCsv() so context-menu Export and toolbar
    // Export CSV produce identical files. Also add "Export visible page
    // (fast)" alias for the rare case someone wants only what they see.
    items.push(
      'copy', 'copyWithHeaders', 'separator',
      {
        name: 'Export CSV (all matching filter)',
        icon: '<span style="color:#60a5fa">⬇</span>',
        action: () => this.zone.run(() => this.onExportCsv('csv')),
      },
      {
        name: 'Export Excel (.xls, all matching filter)',
        icon: '<span style="color:#98c379">⬇</span>',
        action: () => this.zone.run(() => this.onExportCsv('xls')),
      },
      {
        name: 'Export visible page only (fast)',
        action: () => this.zone.run(() => {
          try { params?.api?.exportDataAsCsv?.(); } catch (e) { console.warn('page-only export failed', e); }
        }),
      },
    );
    return items;
  }

  /** Extract a row's user email. Straight off the CommerceEvent.email
   *  field — the wrapping pipeline resolves woocommerce billing_email /
   *  stripe customer_email / kartra email / samcart email into this one
   *  slot before the event lands in Mongo, so downstream consumers only
   *  ever see the canonical email. Empty/undefined → skip. */
  private _rowEmail(row: any): string {
    return String(row?.email || '').trim().toLowerCase();
  }

  /** Case-insensitive dedupe preserving first-seen order. Empty entries
   *  dropped so a row without an email doesn't force a `,,` into the URL. */
  private _dedupeEmails(list: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of list) {
      const em = String(raw || '').trim().toLowerCase();
      if (!em || seen.has(em)) continue;
      seen.add(em);
      out.push(em);
    }
    return out;
  }

  /** Minimal HTML-escape for menu labels — email is operator/webhook input.
   *  Mirror of the helper in error-reports.component. */
  private _htmlEscape(s: string): string {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  init_grid() {
    this.grid = {
      options: {
        context: this,
        // Server-side pagination (IT-F2-134). Infinite row model: grid
        // pulls blocks via datasource.getRows() as the operator scrolls.
        // See _buildDatasource() for the fetch shape.
        rowModelType: 'infinite',
        cacheBlockSize: CACHE_BLOCK_SIZE,
        maxBlocksInCache: MAX_BLOCKS_IN_CACHE,
        // MUST be >= 1 — the AG-Grid infinite-row-model source treats a
        // 0-row initial count as "assume 0" and never triggers the first
        // getRows() call, AND logs a validation warning in the console.
        // 1 kicks the initial fetch (which then replaces with the real
        // total from the server-side count).
        infiniteInitialRowCount: 1,
        // Row selection kept as multiRow so operators can still select
        // + copy-JSON etc. across the currently-cached blocks. Note:
        // infinite-model selection is per-block-in-cache; rows that
        // scroll out of the cache lose selection state.
        suppressCellFocus: true,
        rowSelection: {
          mode: 'multiRow',
          checkboxes: true,
          headerCheckbox: false,      // header-checkbox meaningless when not all rows are loaded
          enableClickSelection: false,
        },
        defaultColDef: {
          floatingFilter: true,
          filter: true,
          sortable: true,
          resizable: true,
          cellStyle: { 'text-align': 'left' },
        },
        rowClassRules: {
          'live-row-fresh': (params: any) => {
            const u = params?.data?._freshUntil;
            return typeof u === 'number' && Date.now() < u;
          },
        },
        // Per-row tint for rows the operator hasn't seen yet — driven
        // by UserPrefsService arrival snapshot + CustomerColorsService.
        // Sibling to error-reports.component.
        getRowStyle: (params: any) => this.rowStyleForNew(params),
        getRowId: (params: any) => params.data._id,
        onRowClicked: (e: any) => { if (e?.data) this.onSelect(e.data); },
        onSelectionChanged: (e: any) => {
          this.zone.run(() => { this.selectedCount = e?.api?.getSelectedRows?.()?.length || 0; });
        },
        onGridReady: (e: any) => {
          this.grid.api = e.api;
          // Wire the pending datasource if ngOnInit resolved it before
          // the grid mounted. Idempotent — no-op if already installed.
          this._wireDatasourceIfReady();
          // IT-F2 Mike 2026-11-02 — apply ?filter + ?sort decoded in
          // ngOnInit now that the grid API is live.
          this._applyPendingGridState();
        },
        // IT-F2 Mike 2026-11-02 — mirror filter + sort changes to the
        // URL via replaceUrl so a shared URL reproduces the view AND
        // dropdown fiddling doesn't spam browser history.
        onFilterChanged: () => { this._syncGridStateToUrl(); },
        onSortChanged: () => { this._syncGridStateToUrl(); },
        // Right-click → offer to look up the row's user(s) in the Users
        // panel. When the right-clicked row is part of a selection, use
        // every selected row's email (deduped). Otherwise fall back to
        // just the right-clicked row. Mirror of the pattern in
        // error-reports.component. Mike 2026-07-05.
        getContextMenuItems: (params: any) => this._buildContextMenu(params),
      },
      // No statusBar — agTotalRowCountComponent + agFilteredRowCountComponent
      // are clientSide-row-model only (they call rowModel.forEachNode
      // AfterFilter which the infinite model doesn't expose → the panel
      // constructor throws inside createGrid() and kills grid init
      // silently before onGridReady fires). Total count renders in the
      // toolbar via `totalCount` instead — see commerce-events.component.html.
      // Fleet gotcha: feedback_aggrid_status_panels_infinite_incompat.md.
      statusBar: undefined,
      columnDefs: [
        // Narrow NEW indicator column — sibling to the one in
        // error-reports.component. Renders a small pill for rows
        // stamped after the operator's arrival snapshot.
        {
          headerName: '',
          width: 50,
          suppressSizeToFit: true,
          sortable: false,
          filter: false,
          resizable: false,
          cellStyle: { 'text-align': 'center', 'padding': '0 4px' },
          cellRenderer: (params: any) => {
            if (!this._isNewSinceArrival(params?.data)) return '';
            return '<span style="display:inline-block;padding:1px 6px;border-radius:9999px;background:#dc2626;color:#fff;font-size:9px;font-weight:700;letter-spacing:.04em;line-height:14px;vertical-align:middle;">NEW</span>';
          },
        },
        {
          field: '_id', headerName: 'ID', width: 160, suppressSizeToFit: true,
          filter: 'agTextColumnFilter', cellClass: 'font-mono',
          cellStyle: { 'text-align': 'left', color: '#5eead4' },
          valueFormatter: (p: any) => p.value ? String(p.value).slice(0, 12) + '…' : '',
          tooltipField: '_id',
        },
        {
          field: 'created_at', headerName: 'When', width: 130, suppressSizeToFit: true,
          filter: 'agDateColumnFilter',
          valueFormatter: (p: any) => this.formatRelative(p.value),
          tooltipValueGetter: (p: any) => this.formatFullWhen(p.value),
          sort: 'desc' as any, sortIndex: 0,
        },
        {
          field: 'customer', headerName: 'Customer', width: 140, filter: 'agTextColumnFilter',
          // Color-coded pill — mirrors Users/Error Reports convention.
          cellRenderer: (params: any) => {
            const slug = params?.value;
            if (!slug) return '<span class="text-gray-400">—</span>';
            const color = this.customerColors.brandColorFor(slug) || (slug === 'f2' ? '#94a3b8' : '#7c3aed');
            const label = slug === 'f2' ? 'F2 (legacy)' : slug;
            const style = `background:${color}1a;color:${color};border:1px solid ${color}33`;
            return `<span class="px-1.5 py-0.5 rounded text-[10px] font-mono" style="${style}">${label}</span>`;
          },
        },
        { field: 'integration', headerName: 'Integration', width: 120, filter: 'agTextColumnFilter' },
        { field: 'topic', headerName: 'Topic', width: 160, filter: 'agTextColumnFilter', cellClass: 'font-mono' },
        { field: 'status', headerName: 'Status', width: 130, filter: 'agTextColumnFilter', cellClass: 'font-mono' },
        {
          field: 'action', headerName: 'Action', width: 120, filter: 'agTextColumnFilter',
          cellClassRules: {
            'text-emerald-400': (p: any) => p.value === 'activate',
            'text-red-400':     (p: any) => p.value === 'deactivate',
            'text-gray-400':    (p: any) => p.value === 'noop',
          },
        },
        {
          field: 'result', headerName: 'Result', width: 200, filter: 'agTextColumnFilter',
          cellClassRules: {
            'text-emerald-400': (p: any) => p.value === 'success',
            'text-red-400':     (p: any) => p.value === 'failed',
            'text-amber-400':   (p: any) => p.value === 'partial',
            'text-gray-400':    (p: any) => p.value === 'noop',
            'text-teal-300':    (p: any) => p.value === 'already_activated',
            'text-slate-400':   (p: any) => p.value === 'already_deactivated',
            'text-violet-300':  (p: any) => p.value === 'test',
          },
          // Append compact chips to distinguish subclasses of the base
          // result that would otherwise look identical in the grid:
          //   - indigo "New User" on activate rows that provisioned a
          //     fresh Cognito user (details.new_user=true)
          //   - amber "Never Provisioned" on revoke rows where the
          //     user isn't in Cognito at all (details.was_never_
          //     provisioned=true) — usually fallout of an earlier
          //     failed activate (auth bug, no api_key). Distinguishes
          //     benign already_deactivated from lost-sale fallout.
          cellRenderer: (params: any) => {
            const v = params?.value ?? '';
            const isNewUser = params?.data?.details?.new_user === true;
            const isNeverProvisioned = params?.data?.details?.was_never_provisioned === true;
            let chips = '';
            if (isNewUser) {
              chips += ` <span class="ml-1 px-1 py-0.5 rounded text-[9px] font-mono bg-indigo-500/20 border border-indigo-400/40 text-indigo-300" title="A new Cognito user was created for this activation">New User</span>`;
            }
            if (isNeverProvisioned) {
              chips += ` <span class="ml-1 px-1 py-0.5 rounded text-[9px] font-mono bg-amber-500/20 border border-amber-400/40 text-amber-300" title="User isn't in Cognito at all — likely fallout of an earlier failed activate. Investigate whether the original sale hit an error we've since fixed.">Never Provisioned</span>`;
            }
            return chips ? `${v}${chips}` : v;
          },
        },
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 180, filter: 'agTextColumnFilter', cellClass: 'font-mono' },
        // Product column (IT-F2-88) — surfaces line_items[0].name so
        // operators auditing entitlement bugs (e.g. IT-F2-86 Ceres
        // Club noop) can correlate email × product without opening
        // each row. Value is the first line_item's name; +N badge
        // appended when the delivery carried multiple items; tooltip
        // shows all line_items (id + name) for the full picture.
        {
          colId: 'product', headerName: 'Product', width: 220, minWidth: 160, filter: 'agTextColumnFilter',
          valueGetter: (p: any) => {
            const items = Array.isArray(p.data?.line_items) ? p.data.line_items : [];
            return items[0]?.name || '';
          },
          tooltipValueGetter: (p: any) => {
            const items = Array.isArray(p.data?.line_items) ? p.data.line_items : [];
            if (items.length === 0) return '';
            return items.map((li: any) => `${li?.product_id ?? '?'} — ${li?.name ?? '(unnamed)'}`).join('\n');
          },
          cellRenderer: (params: any) => {
            const items = Array.isArray(params?.data?.line_items) ? params.data.line_items : [];
            if (items.length === 0) return '<span class="text-gray-400">—</span>';
            const name = items[0]?.name || '(unnamed)';
            const pid = items[0]?.product_id != null ? String(items[0].product_id) : '';
            const nameTruncated = name.length > 30 ? name.slice(0, 30) + '…' : name;
            const extra = items.length > 1
              ? ` <span class="ml-1 px-1 py-0.5 rounded text-[9px] font-mono bg-slate-500/20 border border-slate-400/40 text-slate-300" title="This delivery carried ${items.length} line items">+${items.length - 1}</span>`
              : '';
            const pidPill = pid
              ? ` <span class="ml-1 text-[9px] font-mono text-gray-500">${pid}</span>`
              : '';
            // IT-F2-89: amber "Unmapped" chip when the backend flagged
            // this row as touching an unmapped/unassigned product. The
            // sibling SAMCART_UNMAPPED_PRODUCT ER also fires — this
            // chip is the in-grid signal so operators don't need to
            // cross-reference the Error Reports view when triaging.
            const unmapped = Array.isArray(params?.data?.details?.unmapped_products) ? params.data.details.unmapped_products : [];
            let unmappedChip = '';
            if (unmapped.length > 0) {
              const tip = unmapped.map((u: any) => `${u?.product_id || '?'} — ${u?.name || '(unnamed)'}${u?.auto_inserted ? ' (auto-inserted)' : ' (still unassigned)'}`).join('\n');
              const safeTip = String(tip).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
              unmappedChip = ` <span class="ml-1 px-1 py-0.5 rounded text-[9px] font-mono bg-amber-500/20 border border-amber-400/40 text-amber-300" title="${safeTip}">Unmapped</span>`;
            }
            return `<span>${nameTruncated}</span>${pidPill}${extra}${unmappedChip}`;
          },
        },
        {
          colId: 'scanners_join', headerName: 'Scanners', flex: 1, minWidth: 160, filter: 'agTextColumnFilter',
          valueGetter: (p: any) => Array.isArray(p.data?.scanners) ? p.data.scanners.join(', ') : '',
          cellClass: 'font-mono',
        },
        { field: 'error', headerName: 'Error', flex: 1, minWidth: 200, filter: 'agTextColumnFilter', tooltipField: 'error' },
      ],
    };
  }
}
